import $ from 'jquery';
import actions from '@wc-stripe/actions';
import {isNextActionRequired, parseOrderReviewParams} from '@wc-stripe/utils';

/**
 * DOM Events Handler
 *
 * Singleton that listens for browser DOM events and translates them into
 * application actions. Provides a clean separation between browser events
 * and business logic.
 *
 * @since 4.0.0
 */
class DOMEvents {

    constructor() {
        this.initialize();
    }

    /**
     * Initialize DOM event listeners
     */
    initialize() {
        // Cart page events
        //$(document.body).on('updated_wc_div', this.onCartDOMUpdated.bind(this));
        $(document.body).on('updated_cart_totals', this.onCartDOMUpdated.bind(this));

        // Mini-cart events
        $(document.body).on('added_to_cart', this.onAddedToCart.bind(this));
        $(document.body).on('removed_from_cart', this.onRemovedFromCart.bind(this));
        $(document.body).on('wc_fragments_refreshed wc_fragments_loaded', this.onCartFragmentsChanged.bind(this));

        // Checkout page events
        $(document.body).on('update_checkout', this.onUpdateCheckout.bind(this));
        $(document.body).on('updated_checkout', this.onUpdatedCheckout.bind(this));
        $(document.body).on('change', 'input[name="payment_method"]', this.onPaymentMethodSelected.bind(this));
        $(document.body).on('payment_method_selected', this.onPaymentMethodSelected.bind(this));
        $('form.checkout').on('checkout_place_order_success', this.onPlaceOrderSuccess.bind(this));

        // Billing field change events
        $(document.body).on('change', '[name="billing_phone"],[name="shipping_phone"],[name="billing_email"]', this.onCheckoutFieldChange.bind(this));

        // Shipping method events¬
        $(document.body).on('change', 'input[name^="shipping_method"]', this.onShippingMethodChange.bind(this));

        $(document.body).on('checkout_error', this.onCheckoutError.bind(this));

        // Listen for hashchange event used for 3DS integration
        window.addEventListener('hashchange', this.onHashChange.bind(this));

        // Check hash on initial page load (user may land on page with hash)
        if (window.location.hash) {
            // We wait for the stripe_sdk_loaded action to ensure the stripe.js sdk has been loaded.
            actions.addAction('stripe_sdk_loaded', (e) => this.onHashChange(e), 20);
        }

        if (window.location.search && this.queryHasReviewParams()) {
            $(() => {
                this.processOrderReview();
            });
        }

        // iAPI bridge — translate CustomEvents from ES module view scripts into
        // actions that gateway controllers can subscribe to via actions.addAction().
        document.addEventListener('wc-stripe:mini-cart-ready', ({detail}) => {
            actions.doAction('mini_cart_ready', detail);
        });
        document.addEventListener('wc-stripe:mini-cart-updated', ({detail}) => {
            actions.doAction('mini_cart_updated', detail.cart);
        });
        document.addEventListener('wc-stripe:single-product-variation-changed', ({detail}) => {
            actions.doAction('single_product_variation_changed', detail.variationId, detail.variation);
        });
        document.addEventListener('wc-stripe:single-product-variation-reset', () => {
            actions.doAction('single_product_variation_reset');
        });
    }

    onCartDOMUpdated(e) {
        let data = null;
        if (window?.wcStripeCartData?.cart) {
            data = window.wcStripeCartData;
            window.wcStripeCartData = null;
        }
        actions.doAction('cart_dom_updated', e, data);
    }

    onAddedToCart(e, fragments) {
        const data = fragments?.wc_stripe_data || {};
        actions.doAction('added_to_cart', e, data);
    }

    onRemovedFromCart(e, fragments) {
        const data = fragments?.wc_stripe_data || {};
        actions.doAction('removed_from_cart', e, data);
    }

    /**
     * Triggered before WooCommerce sends the updated_checkout event.
     * @param e
     */
    onUpdateCheckout(e) {
        actions.doAction('update_checkout', e);
    }

    /**
     * Event that is triggered after WooCommerce has processed the update_order_review ajax request.
     * @param e
     * @param data
     */
    onUpdatedCheckout(e, data = {}) {
        actions.doAction('updated_checkout', e, data);
    }

    onPaymentMethodSelected(e) {
        actions.doAction('payment_method_selected', $('[name="payment_method"]:checked').val());
    }

    onCartFragmentsChanged(e) {
        actions.doAction('cart_fragments_changed', e);
    }

    onCheckoutFieldChange(e) {
        actions.doAction('checkout_field_changed', e.currentTarget);
    }

    onShippingMethodChange(e) {
        actions.doAction('shipping_method_changed', $(e.currentTarget).val());
    }

    onCheckoutError(e, errorMessage) {
        const paymentMethod = $('[name="payment_method"]:checked').val();
        actions.doAction('checkout_error_' + paymentMethod, errorMessage);
    }

    /**
     *
     * @param e
     * @param {Object} result
     * @param form
     */
    onPlaceOrderSuccess(e, result, form) {
        const data = isNextActionRequired(result?.redirect || '');
        if (!data || !data.hasOwnProperty('client_secret')) {
            return true;
        }
        // Flag so onHashChange skips this - WooCommerce will set the hash after we return true
        this._intentHandled = true;
        // Emit action - let controllers decide what to do with this data
        if (data.type === 'payment_intent') {
            actions.doAction('payment_intent_requires_actions', data);
        } else {
            actions.doAction('setup_intent_requires_actions', data);
        }
        return true;
    }

    /**
     * Handle URL hash change event
     *
     * When Stripe requires 3DS authentication, the server redirects back with a URL hash
     * containing base64-encoded payment data in format: #response=base64(JSON)
     *
     * This method:
     * 1. Parses the hash to extract response data
     * 2. Decodes base64 and parses JSON
     * 3. Validates the data structure
     * 4. Cleans the hash from URL (prevents sensitive data in browser history)
     * 5. Emits action for controllers to handle
     *
     * Expected JSON structure:
     * {
     *   client_secret: "pi_xxx_secret_xxx",
     *   gateway_id: "stripe_cc",
     *   type: "payment_intent" | "setup_intent",
     *   order_id: 123,
     *   order_key: "wc_order_xxx"
     * }
     *
     * @param {Event} e - Hash change event (may be undefined on initial load)
     */
    onHashChange(e) {
        try {
            const data = isNextActionRequired(window.location.hash);

            if (!data || !data.hasOwnProperty('client_secret')) {
                return;
            }

            // Clean hash from URL to prevent sensitive data in browser history.
            // Uses replaceState (not pushState) so the hash-bearing URL isn't left behind
            // as a separate history entry the user could navigate back into.
            history.replaceState({}, '', window.location.pathname);

            // onPlaceOrderSuccess already handled this - skip to avoid double-firing
            if (this._intentHandled) {
                this._intentHandled = false;
                return;
            }

            // Emit action - let controllers decide what to do with this data
            if (data.type === 'payment_intent') {
                actions.doAction('payment_intent_requires_actions', data);
            } else {
                actions.doAction('setup_intent_requires_actions', data);
            }
        } catch (error) {
            // Silently fail on parse errors (could be unrelated hash)
            console.error('Error parsing 3DS response hash:', error);
        }
    }

    processOrderReview() {
        const params = parseOrderReviewParams(window.location.search || '');
        if (params) {
            actions.doAction('checkout_order_review', params);
        }
    }

    queryHasReviewParams() {
        return parseOrderReviewParams(window.location.search || '') !== null;
    }
}

new DOMEvents();